import moment from "moment";
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { Button, Modal, Pagination, Table } from "flowbite-react";
import axios from "../../config/axiosInstance";
import { useQuery, useQueryClient } from "react-query";
import { ADMIN, CATEGORIES, USERS } from "../../config/apiConfig";
import { TbLockSearch } from "react-icons/tb";
import Drawer from "../Drawer";
import CompanyQueriesForm from "../forms/CompanyQueriesForm";
import { IoMdAdd, IoMdSearch } from "react-icons/io";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  MdOutlineRemoveRedEye,
  MdClose,
  MdCheckCircleOutline,
  MdOutlineRestore,
  MdOutlineBolt,
  MdArrowDropUp,
  MdArrowDropDown,
} from "react-icons/md";
import { LuFilter, LuFilterX } from "react-icons/lu";
import ModalDelete from "../modals/ModalDelete";
import TableLoading from "../TableLoading";
import { Tooltip } from "react-tooltip";
import CompanyInvoiceForm from "../forms/CompanyInvoiceForm";
import { useForm } from "react-hook-form";
import SelectInput from "../inputs/SelectInput";
import AutoCompleteInput from "../inputs/Autocomplete";
import { useApplication } from "../../contexts/ApplicationContext";
import EmptyTable from "./EmptyTable";
import CompanyEditForm from "../forms/CompanyEditForm";
import DateRangeInput from "../inputs/DatePickerInput";
import { formatToReal } from "../../utils/utils";
import "react-datepicker/dist/react-datepicker.css";

const STORAGE_KEY = "adminCustomers.filters.v1";
const STORAGE_OPEN_KEY = "adminCustomers.filtersOpen.v1";
const STORAGE_VIEWS_KEY = "adminCustomers.savedViews.v1";

/* --- Micro UI helpers --- */
function Chip({ label, onClear, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    fuchsia: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border ${tones[tone]} whitespace-nowrap`}
    >
      {label}
      {!!onClear && (
        <button
          onClick={onClear}
          type="button"
          aria-label={`Remover filtro ${label}`}
          className="hover:opacity-70"
        >
          <MdClose size={14} />
        </button>
      )}
    </span>
  );
}

const AdminCustomersTable = () => {
  // URL sync
  const [searchParams, setSearchParams] = useSearchParams();

  const { userPermission, user } = useApplication();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // paginação (lendo da URL)
  const pageFromUrl = Number(searchParams.get("page") || 1);
  const [currentPage, setCurrentPage] = useState(
    Number.isNaN(pageFromUrl) || pageFromUrl <= 0 ? 1 : pageFromUrl
  );
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const onPageChange = (page) => {
    setCurrentPage(page);
    const obj = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...obj, page: String(page) });
  };

  // busca (lendo da URL)
  const [searchName, setSearchName] = useState(
    searchParams.get("search") || ""
  );
  const [nameParam, setNameParam] = useState(searchParams.get("search") || "");

  const [isDrawerPermissionQueries, setIsDrawerPermissionQueries] =
    useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isDrawerViewInvoice, setIsDrawerViewInvoice] = useState(false);
  const [isDrawerEditCustomer, setIsDrawerEditCustomer] = useState(false);

  const [isOpenModalBlock, setOpenModalBlock] = useState(false);
  const [isLoadingBlock, setIsLoadingBlock] = useState(false);
  const [typeBlock, setTypeBlock] = useState(0);

  // representante e período vindos da URL
  const initialCreatedAtStart = searchParams.get("createdAtStart")
    ? moment(searchParams.get("createdAtStart"), "YYYY-MM-DD").toDate()
    : null;
  const initialCreatedAtEnd = searchParams.get("createdAtEnd")
    ? moment(searchParams.get("createdAtEnd"), "YYYY-MM-DD").toDate()
    : null;
  const initialRepresentative = {
    label: searchParams.get("representativeName") || "Todos os representantes",
    value: searchParams.get("representativeId") || "",
  };
  const [selectedRepresentative, setSelectedRepresentative] = useState(
    initialRepresentative
  );
  const [createdAtRange, setCreatedAtRange] = useState([
    initialCreatedAtStart,
    initialCreatedAtEnd,
  ]);
  const [appliedCreatedAtRange, setAppliedCreatedAtRange] = useState([
    initialCreatedAtStart,
    initialCreatedAtEnd,
  ]);
  const [appliedRepresentative, setAppliedRepresentative] = useState(
    initialRepresentative
  );
  const [createdAtStart, createdAtEnd] = createdAtRange;
  const [appliedCreatedAtStart, appliedCreatedAtEnd] = appliedCreatedAtRange;

  // estado do colapso do filtro (persistente)
  const [isFiltersOpen, setIsFiltersOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_OPEN_KEY);
      return raw ? JSON.parse(raw) === true : true;
    } catch {
      return false;
    }
  });

  const itemsPerPage = 20;

  const { register, setValue, getValues } = useForm({
    defaultValues: {
      status: searchParams.get("status") || "",
      typePayment: searchParams.get("typePayment") || "",
      typeCustomer: searchParams.get("typeCustomer") || "",
      responsibleId: searchParams.get("responsibleId") || "",
      isOnline: searchParams.get("isOnline") || "", // '', '1', '0'
    },
  });
  const [appliedFilters, setAppliedFilters] = useState({
    status: searchParams.get("status") || "",
    typePayment: searchParams.get("typePayment") || "",
    typeCustomer: searchParams.get("typeCustomer") || "",
    responsibleId: searchParams.get("responsibleId") || "",
    isOnline: searchParams.get("isOnline") || "",
  });
  const [sortConfig, setSortConfig] = useState({
    key: "",
    direction: "asc",
  });

  // ===== Helpers de persistência =====
  const restoringFromStorageRef = useRef(false);

  const hasAnyFilterInUrl = useMemo(() => {
    const keys = [
      "search",
      "createdAtStart",
      "createdAtEnd",
      "status",
      "typePayment",
      "typeCustomer",
      "responsibleId",
      "isOnline",
      "representativeId",
    ];
    return keys.some((k) => searchParams.get(k));
  }, [searchParams]);

  // Restaura de localStorage se não houver filtros na URL
  useEffect(() => {
    if (hasAnyFilterInUrl) return; // já está vindo da URL, não sobrescrever
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);

      restoringFromStorageRef.current = true;

      // restaura campos do form
      setValue("status", saved.status ?? "");
      setValue("typePayment", saved.typePayment ?? "");
      setValue("typeCustomer", saved.typeCustomer ?? "");
      setValue("responsibleId", saved.responsibleId ?? "");
      setValue("isOnline", saved.isOnline ?? "");

      // datas
      const start = saved.createdAtStart
        ? moment(saved.createdAtStart, "YYYY-MM-DD").toDate()
        : null;
      const end = saved.createdAtEnd
        ? moment(saved.createdAtEnd, "YYYY-MM-DD").toDate()
        : null;
      setCreatedAtRange([start, end]);
      setAppliedCreatedAtRange([start, end]);

      // representante
      setSelectedRepresentative(
        saved.selectedRepresentative ?? {
          label: "Todos os representantes",
          value: "",
        }
      );
      setAppliedRepresentative(
        saved.selectedRepresentative ?? {
          label: "Todos os representantes",
          value: "",
        }
      );

      setAppliedFilters({
        status: saved.status ?? "",
        typePayment: saved.typePayment ?? "",
        typeCustomer: saved.typeCustomer ?? "",
        responsibleId: saved.responsibleId ?? "",
        isOnline: saved.isOnline ?? "",
      });

      // aplica na URL para manter padrão do seu app
      const next = {
        page: "1",
        ...(saved.createdAtStart
          ? { createdAtStart: saved.createdAtStart }
          : {}),
        ...(saved.createdAtEnd ? { createdAtEnd: saved.createdAtEnd } : {}),
        ...(saved.status ? { status: saved.status } : {}),
        ...(saved.typePayment ? { typePayment: saved.typePayment } : {}),
        ...(saved.typeCustomer ? { typeCustomer: saved.typeCustomer } : {}),
        ...(saved.responsibleId ? { responsibleId: saved.responsibleId } : {}),
        ...(saved.isOnline ? { isOnline: saved.isOnline } : {}),
        ...(userPermission === "POWER_USER" &&
        saved.selectedRepresentative?.value
          ? {
              representativeId: saved.selectedRepresentative.value,
              representativeName: saved.selectedRepresentative.label || "",
            }
          : {}),
      };
      setSearchParams(next);
    } catch (e) {
      // ignore
    } finally {
      setTimeout(() => {
        restoringFromStorageRef.current = false;
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Salva filtros no localStorage sempre que mudarem
  useEffect(() => {
    const payload = {
      createdAtStart: appliedCreatedAtStart
        ? moment(appliedCreatedAtStart).format("YYYY-MM-DD")
        : "",
      createdAtEnd: appliedCreatedAtEnd
        ? moment(appliedCreatedAtEnd).format("YYYY-MM-DD")
        : "",
      status: appliedFilters.status || "",
      typePayment: appliedFilters.typePayment || "",
      typeCustomer: appliedFilters.typeCustomer || "",
      responsibleId: appliedFilters.responsibleId || "",
      isOnline: appliedFilters.isOnline || "",
      selectedRepresentative:
        userPermission === "POWER_USER" ? appliedRepresentative : undefined,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {}
  }, [
    appliedCreatedAtStart,
    appliedCreatedAtEnd,
    appliedFilters,
    appliedRepresentative,
    userPermission,
  ]);

  // Persiste o estado aberto/fechado
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_OPEN_KEY, JSON.stringify(isFiltersOpen));
    } catch {}
  }, [isFiltersOpen]);

  // ===== Queries =====
  const { isLoading: isLoadingListCustomers, data: listCustomers } = useQuery(
    [
      "listCustomers",
      currentPage,
      nameParam,
      appliedCreatedAtStart
        ? moment(appliedCreatedAtStart).format("YYYY-MM-DD")
        : "",
      appliedCreatedAtEnd
        ? moment(appliedCreatedAtEnd).format("YYYY-MM-DD")
        : "",
      appliedFilters.status,
      appliedFilters.typePayment,
      appliedFilters.typeCustomer,
      appliedFilters.responsibleId,
      appliedFilters.isOnline,
      appliedRepresentative?.value,
      sortConfig.key,
      sortConfig.direction,
    ],
    async () => {
      const start = appliedCreatedAtStart
        ? moment(appliedCreatedAtStart).format("YYYY-MM-DD")
        : "";
      const end = appliedCreatedAtEnd
        ? moment(appliedCreatedAtEnd).format("YYYY-MM-DD")
        : "";

      const { data } = await axios.get(
        `${ADMIN.GET_CUSTOMERS}?page=${currentPage}` +
          `&search=${encodeURIComponent(nameParam || "")}` +
          `&createdAtStart=${start}` +
          `&createdAtEnd=${end}` +
          `&status=${appliedFilters.status || ""}` +
          `&typeCustomer=${appliedFilters.typeCustomer || ""}` +
          `&typePayment=${appliedFilters.typePayment || ""}` +
          `&responsibleId=${appliedFilters.responsibleId || ""}` +
          `&isOnline=${appliedFilters.isOnline || ""}` +
          `&sortBy=${encodeURIComponent(sortConfig.key || "")}` +
          `&sortDir=${encodeURIComponent(sortConfig.direction || "")}` +
          `&representativeId=${
            userPermission === "POWER_USER"
              ? appliedRepresentative?.value || ""
              : ""
          }` +
          `&itemsPerPage=${itemsPerPage}`
      );

      setTotalPages(data.data.totalPages || 1);
      setTotalCount(data.data.totalCount || 0);

      return data.data.data;
    }
  );

  const { isLoading: isLoadingListRepresentatives, data: listRepresentatives } =
    useQuery(
      ["listRepresentatives"],
      async () => {
        const { data } = await axios.get(
          `${ADMIN.GET_REPRESENTATIVES}?itemsPerPage=-1`
        );
        const representatives = [
          { label: "Todos os representantes", value: "" },
          { label: "Relinq Score", value: 1 },
          ...data.data.data.map((representative) => ({
            value: representative.id,
            label: representative?.name || representative?.socialReason,
          })),
        ];
        return representatives;
      },
      { enabled: userPermission === "POWER_USER" }
    );

  const { data: categories } = useQuery(["categories"], async () => {
    const { data } = await axios.get(
      `${CATEGORIES.GET_ALL}?itemsPerPage=${-1}`
    );
    return data.data.data;
  });

  const { isLoading: isLoadingUsers, data: users } = useQuery(
    ["usersAll", user],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_USERS}?itemsPerPage=${-1}&companyId=${
          user?.companyId
        }&status=1`
      );
      return (data?.data?.data || []).map((u) => ({
        id: u.id,
        name: u.name || u.email || `Usuário ${u.id}`,
      }));
    }
  );

  // manter label coerente se veio só id na URL
  useEffect(() => {
    if (
      userPermission === "POWER_USER" &&
      listRepresentatives &&
      selectedRepresentative?.value &&
      !selectedRepresentative?.label
    ) {
      const found = listRepresentatives.find(
        (r) => String(r.value) === String(selectedRepresentative.value)
      );
      if (found) {
        setSelectedRepresentative(found);
        setAppliedRepresentative(found);
        setSearchParams((prev) => {
          const obj = Object.fromEntries(prev.entries());
          obj.representativeName = found.label;
          return obj;
        });
      }
    }
  }, [
    listRepresentatives,
    selectedRepresentative?.value,
    userPermission,
    setSearchParams,
  ]);

  // helper para aplicar filtros na URL mantendo layout
  const applyFiltersToUrl = useCallback(
    (overrides = {}) => {
      if (restoringFromStorageRef.current) return; // evita loop na restauração
      const resolveValue = (key, fallback) =>
        Object.prototype.hasOwnProperty.call(overrides, key)
          ? overrides[key]
          : fallback;
      const start = resolveValue(
        "createdAtStart",
        appliedCreatedAtStart
          ? moment(appliedCreatedAtStart).format("YYYY-MM-DD")
          : ""
      );
      const end = resolveValue(
        "createdAtEnd",
        appliedCreatedAtEnd
          ? moment(appliedCreatedAtEnd).format("YYYY-MM-DD")
          : ""
      );
      const base = Object.fromEntries(searchParams.entries());
      const next = {
        ...base,
        page: "1",
        search: resolveValue("search", nameParam || ""),
        createdAtStart: start || "",
        createdAtEnd: end || "",
        status: resolveValue("status", appliedFilters.status || ""),
        typePayment: resolveValue(
          "typePayment",
          appliedFilters.typePayment || ""
        ),
        typeCustomer: resolveValue(
          "typeCustomer",
          appliedFilters.typeCustomer || ""
        ),
        responsibleId: resolveValue(
          "responsibleId",
          appliedFilters.responsibleId || ""
        ),
        isOnline: resolveValue("isOnline", appliedFilters.isOnline || ""),
        ...(userPermission === "POWER_USER"
          ? {
              representativeId: resolveValue(
                "representativeId",
                appliedRepresentative?.value || ""
              ),
              representativeName: resolveValue(
                "representativeName",
                appliedRepresentative?.label || ""
              ),
            }
          : { representativeId: "", representativeName: "" }),
      };
      Object.keys(next).forEach((k) => {
        if (next[k] === "" || next[k] == null) delete next[k];
      });
      setSearchParams(next);
      setCurrentPage(1);
    },
    [
      appliedCreatedAtStart,
      appliedCreatedAtEnd,
      appliedFilters,
      appliedRepresentative,
      nameParam,
      searchParams,
      setSearchParams,
      setCurrentPage,
      userPermission,
    ]
  );

  // busca por texto -> atualiza URL
  const handleSearchName = (e) => {
    e.preventDefault();
    const values = getValues();
    const start = createdAtStart
      ? moment(createdAtStart).format("YYYY-MM-DD")
      : "";
    const end = createdAtEnd ? moment(createdAtEnd).format("YYYY-MM-DD") : "";
    const rep =
      userPermission === "POWER_USER"
        ? selectedRepresentative
        : { label: "Todos os representantes", value: "" };
    setNameParam(searchName);
    setAppliedFilters({
      status: values.status || "",
      typePayment: values.typePayment || "",
      typeCustomer: values.typeCustomer || "",
      responsibleId: values.responsibleId || "",
      isOnline: values.isOnline || "",
    });
    setAppliedCreatedAtRange([createdAtStart, createdAtEnd]);
    setAppliedRepresentative(rep);
    applyFiltersToUrl({
      search: searchName || "",
      createdAtStart: start,
      createdAtEnd: end,
      status: values.status || "",
      typePayment: values.typePayment || "",
      typeCustomer: values.typeCustomer || "",
      responsibleId: values.responsibleId || "",
      isOnline: values.isOnline || "",
      representativeId: userPermission === "POWER_USER" ? rep?.value || "" : "",
      representativeName:
        userPermission === "POWER_USER" ? rep?.label || "" : "",
    });
  };

  const handleEditPermissionQueries = (company) => {
    setSelectedCompany(company);
    setIsDrawerPermissionQueries((prevState) => !prevState);
  };

  const blockUnlockCustomer = async (type = 0) => {
    try {
      setIsLoadingBlock(true);
      await axios.put(`${ADMIN.GET_COMPANY}/${selectedCompany.id}`, {
        status: type,
      });
      queryClient.invalidateQueries(["listCustomers", currentPage]);
      setOpenModalBlock(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingBlock(false);
    }
  };

  const clearFilters = () => {
    setSearchName("");
    setNameParam("");
    setValue("status", "");
    setValue("typePayment", "");
    setValue("typeCustomer", "");
    setValue("responsibleId", "");
    setValue("isOnline", "");
    setCreatedAtRange([null, null]);
    setSelectedRepresentative({ label: "Todos os representantes", value: "" });
    setAppliedFilters({
      status: "",
      typePayment: "",
      typeCustomer: "",
      responsibleId: "",
      isOnline: "",
    });
    setAppliedCreatedAtRange([null, null]);
    setAppliedRepresentative({ label: "Todos os representantes", value: "" });
    setCurrentPage(1);
    applyFiltersToUrl({
      search: "",
      createdAtStart: "",
      createdAtEnd: "",
      status: "",
      typePayment: "",
      typeCustomer: "",
      responsibleId: "",
      isOnline: "",
      representativeId: "",
      representativeName: "",
    });
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  };

  // ===== UI helpers =====
  const activeFilters = useMemo(() => {
    const entries = [];
    if (nameParam)
      entries.push({
        key: "search",
        tone: "slate",
        label: `Busca: ${nameParam}`,
      });
    if (appliedCreatedAtStart || appliedCreatedAtEnd)
      entries.push({
        key: "date",
        tone: "slate",
        label: `${
          appliedCreatedAtStart
            ? moment(appliedCreatedAtStart).format("DD/MM/YYYY")
            : "—"
        } – ${
          appliedCreatedAtEnd
            ? moment(appliedCreatedAtEnd).format("DD/MM/YYYY")
            : "—"
        }`,
      });
    if (appliedFilters.typeCustomer)
      entries.push({
        key: "typeCustomer",
        tone: "indigo",
        label: appliedFilters.typeCustomer === "CPF" ? "Física" : "Jurídica",
      });
    if (appliedFilters.typePayment)
      entries.push({
        key: "typePayment",
        tone: "blue",
        label: appliedFilters.typePayment === "PRE" ? "Pré-Pago" : "Pós-Pago",
      });
    if (String(appliedFilters.status) !== "")
      entries.push({
        key: "status",
        tone: "emerald",
        label:
          Number(appliedFilters.status) === 1
            ? "Ativo"
            : Number(appliedFilters.status) === 0
            ? "Bloqueado"
            : "Inadimplente",
      });
    if (appliedFilters.responsibleId)
      entries.push({
        key: "responsibleId",
        tone: "amber",
        label: `Resp. #${appliedFilters.responsibleId}`,
      });
    if (String(appliedFilters.isOnline) !== "")
      entries.push({
        key: "isOnline",
        tone: "fuchsia",
        label: appliedFilters.isOnline === "1" ? "Online" : "Offline",
      });
    if (userPermission === "POWER_USER" && appliedRepresentative?.value)
      entries.push({
        key: "representativeId",
        tone: "slate",
        label: appliedRepresentative?.label,
      });
    return entries;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nameParam,
    appliedCreatedAtStart,
    appliedCreatedAtEnd,
    appliedFilters,
    appliedRepresentative,
    userPermission,
  ]);

  const activeFiltersCount = activeFilters.length;

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
    setCurrentPage(1);
    setSearchParams((prev) => {
      const obj = Object.fromEntries(prev.entries());
      obj.page = "1";
      return obj;
    });
  };

  const getSortIconClass = (key, direction) => {
    if (sortConfig.key !== key) return "text-slate-300";
    return sortConfig.direction === direction
      ? "text-slate-700"
      : "text-slate-300";
  };

  const removeSingleFilter = (key) => {
    let nextSearch = nameParam;
    let nextStart = appliedCreatedAtStart;
    let nextEnd = appliedCreatedAtEnd;
    const nextAppliedFilters = { ...appliedFilters };
    let nextRepresentative = appliedRepresentative;

    switch (key) {
      case "search":
        setSearchName("");
        nextSearch = "";
        break;
      case "date":
        setCreatedAtRange([null, null]);
        nextStart = null;
        nextEnd = null;
        break;
      case "typeCustomer":
        setValue("typeCustomer", "");
        nextAppliedFilters.typeCustomer = "";
        break;
      case "typePayment":
        setValue("typePayment", "");
        nextAppliedFilters.typePayment = "";
        break;
      case "status":
        setValue("status", "");
        nextAppliedFilters.status = "";
        break;
      case "responsibleId":
        setValue("responsibleId", "");
        nextAppliedFilters.responsibleId = "";
        break;
      case "isOnline":
        setValue("isOnline", "");
        nextAppliedFilters.isOnline = "";
        break;
      case "representativeId":
        setSelectedRepresentative({
          label: "Todos os representantes",
          value: "",
        });
        nextRepresentative = { label: "Todos os representantes", value: "" };
        break;
      default:
        break;
    }
    setNameParam(nextSearch);
    setAppliedFilters(nextAppliedFilters);
    setAppliedCreatedAtRange([nextStart, nextEnd]);
    setAppliedRepresentative(nextRepresentative);
    applyFiltersToUrl({
      search: nextSearch || "",
      createdAtStart: nextStart ? moment(nextStart).format("YYYY-MM-DD") : "",
      createdAtEnd: nextEnd ? moment(nextEnd).format("YYYY-MM-DD") : "",
      status: nextAppliedFilters.status || "",
      typePayment: nextAppliedFilters.typePayment || "",
      typeCustomer: nextAppliedFilters.typeCustomer || "",
      responsibleId: nextAppliedFilters.responsibleId || "",
      isOnline: nextAppliedFilters.isOnline || "",
      representativeId:
        userPermission === "POWER_USER" ? nextRepresentative?.value || "" : "",
      representativeName:
        userPermission === "POWER_USER" ? nextRepresentative?.label || "" : "",
    });
  };

  // ==== Presets de período ====
  const applyDatePreset = (preset) => {
    const now = new Date();
    const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
    if (preset === "7d")
      setCreatedAtRange([moment(now).subtract(7, "days").toDate(), now]);
    if (preset === "30d")
      setCreatedAtRange([moment(now).subtract(30, "days").toDate(), now]);
    if (preset === "mtd") setCreatedAtRange([startOf(now), now]);
    if (preset === "prevM") {
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endPrev = new Date(now.getFullYear(), now.getMonth(), 0);
      setCreatedAtRange([prev, endPrev]);
    }
  };

  // ==== Saved Views (salvar combinação atual de filtros) ====
  const [views, setViews] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_VIEWS_KEY) || "[]");
    } catch {
      return [];
    }
  });

  const applyView = (v) => {
    if (!v) return;
    const nextSearch = v.data.searchName || "";
    const nextStart = v.data.createdAtStart
      ? moment(v.data.createdAtStart, "YYYY-MM-DD").toDate()
      : null;
    const nextEnd = v.data.createdAtEnd
      ? moment(v.data.createdAtEnd, "YYYY-MM-DD").toDate()
      : null;
    const nextFilters = {
      status: v.data.status || "",
      typePayment: v.data.typePayment || "",
      typeCustomer: v.data.typeCustomer || "",
      responsibleId: v.data.responsibleId || "",
      isOnline: v.data.isOnline || "",
    };
    const nextRepresentative =
      userPermission === "POWER_USER"
        ? v.data.representative || {
            label: "Todos os representantes",
            value: "",
          }
        : { label: "Todos os representantes", value: "" };

    setSearchName(nextSearch);
    setNameParam(nextSearch);
    setCreatedAtRange([nextStart, nextEnd]);
    setAppliedCreatedAtRange([nextStart, nextEnd]);
    setValue("status", nextFilters.status);
    setValue("typePayment", nextFilters.typePayment);
    setValue("typeCustomer", nextFilters.typeCustomer);
    setValue("responsibleId", nextFilters.responsibleId);
    setValue("isOnline", nextFilters.isOnline);
    setAppliedFilters(nextFilters);
    if (userPermission === "POWER_USER") {
      setSelectedRepresentative(nextRepresentative);
    }
    setAppliedRepresentative(nextRepresentative);
    applyFiltersToUrl({
      search: nextSearch || "",
      createdAtStart: nextStart ? moment(nextStart).format("YYYY-MM-DD") : "",
      createdAtEnd: nextEnd ? moment(nextEnd).format("YYYY-MM-DD") : "",
      status: nextFilters.status || "",
      typePayment: nextFilters.typePayment || "",
      typeCustomer: nextFilters.typeCustomer || "",
      responsibleId: nextFilters.responsibleId || "",
      isOnline: nextFilters.isOnline || "",
      representativeId:
        userPermission === "POWER_USER" ? nextRepresentative?.value || "" : "",
      representativeName:
        userPermission === "POWER_USER" ? nextRepresentative?.label || "" : "",
    });
  };
  const deleteView = (id) => {
    const next = views.filter((x) => x.id !== id);
    setViews(next);
    try {
      localStorage.setItem(STORAGE_VIEWS_KEY, JSON.stringify(next));
    } catch {}
  };

  return (
    <>
      {/* Botão principal */}
      <div className="flex justify-end mb-3 mt-2">
        <Link
          to="/admin/new-customer"
          className="bg-primary ml-auto md:ml-0 mb-5 md:mb-0 fixed md:relative bottom-4 md:bottom-0 right-6 md:right-0 z-10 md:z-0 rounded-full md:rounded-md shadow-md h-14 md:h-auto w-14 md:w-auto flex md:block justify-center items-center px-4 py-2 text-white"
        >
          <IoMdAdd className="block md:hidden text-2xl" />
          <span className="hidden md:block">Adicionar cliente</span>
        </Link>
      </div>

      {/* ====== FILTROS PRO ====== */}
      <form onSubmit={handleSearchName}>
        <div className="flex flex-col gap-4">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm transition-all duration-500 ease-in-out overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-3 py-2 border-b border-slate-200 bg-gray-50 overflow-hidden">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Filtros Avançados
                </span>
                {activeFiltersCount > 0 && (
                  <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                    {activeFiltersCount} ativo(s)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {views.length > 0 && (
                  <div className="hidden md:flex items-center gap-2">
                    {views.slice(0, 3).map((v) => (
                      <button
                        key={v.id}
                        type="button"
                        className="text-xs px-2.5 py-1 rounded-full border border-gray-200 bg-white hover:bg-gray-50"
                        onClick={() => applyView(v)}
                        title={`Aplicar “${v.name}”`}
                      >
                        <span className="inline-flex items-center gap-1">
                          <MdCheckCircleOutline /> {v.name}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <Button
                  size="xs"
                  className="bg-secondary min-w-24"
                  type="submit"
                  title="Aplicar busca"
                >
                  <div className="flex items-center gap-1">
                    <MdOutlineBolt /> Buscar
                  </div>
                </Button>
                <Button
                  color="light"
                  size="xs"
                  onClick={clearFilters}
                  className="whitespace-nowrap"
                  title="Limpar todos os filtros"
                  type="button"
                >
                  <div className="flex items-center gap-1">
                    <MdOutlineRestore /> Limpar
                  </div>
                </Button>
                <Button
                  color="light"
                  size="xs"
                  type="button"
                  onClick={() => setIsFiltersOpen((prev) => !prev)}
                  className="whitespace-nowrap"
                >
                  <div className="flex items-center gap-2">
                    <span>
                      {isFiltersOpen ? "Esconder filtros" : "Mostrar filtros"}
                    </span>
                    <span className="transition-transform duration-300">
                      {!isFiltersOpen ? <LuFilter /> : <LuFilterX />}
                    </span>
                  </div>
                </Button>
              </div>
            </div>

            {isFiltersOpen && (
              <div className="px-3 py-3">
                {/* Barra superior: busca */}
                <div className="flex flex-col gap-3">
                  {/* Grid de filtros */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 rounded-xl border border-slate-200 bg-slate-50/40 p-3">
                    <div className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-sm">
                      <DateRangeInput
                        label={
                          <span className="text-[13px] font-medium text-slate-600">
                            Data de cadastro
                          </span>
                        }
                        startDate={createdAtStart}
                        endDate={createdAtEnd}
                        onChange={setCreatedAtRange}
                        register={register}
                        nameStart="createdAtStart"
                        nameEnd="createdAtEnd"
                        maxDate={new Date()}
                      />
                    </div>

                    <div className="bg-white/90 rounded-xl border border-slate-200 p-3 shadow-sm">
                      <SelectInput
                        register={register}
                        label="Tipo de cliente"
                        name="typeCustomer"
                      >
                        <option value="">Todos os tipos</option>
                        <option value="CPF">Física</option>
                        <option value="CNPJ">Jurídica</option>
                      </SelectInput>
                    </div>

                    <div className="bg-white/90 rounded-xl border border-slate-200 p-3 shadow-sm">
                      <SelectInput
                        register={register}
                        label="Tipo de pagamento"
                        name="typePayment"
                      >
                        <option value="">Todos os tipos</option>
                        <option value="PRE">Pré-Pago</option>
                        <option value="POS">Pós-Pago</option>
                      </SelectInput>
                    </div>

                    <div className="bg-white/90 rounded-xl border border-slate-200 p-3 shadow-sm">
                      <SelectInput
                        register={register}
                        label="Status do cliente"
                        name="status"
                      >
                        <option value="">Todos os status</option>
                        <option value={1}>Ativo</option>
                        <option value={0}>Bloqueado</option>
                        <option value={-1}>Inadimplente</option>
                      </SelectInput>
                    </div>

                    <div className="bg-white/90 rounded-xl border border-slate-200 p-3 shadow-sm">
                      <SelectInput
                        register={register}
                        label="Responsável pelo Cliente"
                        name="responsibleId"
                        disabled={isLoadingUsers}
                      >
                        <option value="">Todos os responsáveis</option>
                        {(users || []).map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </SelectInput>
                    </div>

                    <div className="bg-white/90 rounded-xl border border-slate-200 p-3 shadow-sm">
                      <SelectInput
                        register={register}
                        label="Cadastro Online"
                        name="isOnline"
                      >
                        <option value="">Todos</option>
                        <option value="1">Sim</option>
                        <option value="0">Não</option>
                      </SelectInput>
                    </div>

                    {userPermission === "POWER_USER" && (
                      <div className="bg-white/90 rounded-xl border border-slate-200 p-3 shadow-sm">
                        <AutoCompleteInput
                          options={listRepresentatives}
                          label="Representante"
                          onChange={(rep) => setSelectedRepresentative(rep)}
                          value={selectedRepresentative?.value}
                          isLoading={isLoadingListRepresentatives}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col md:flex-row gap-2">
                    <div className="flex-1 flex items-center gap-2 rounded-xl bg-white border border-gray-200 px-3 py-2 focus-within:border-indigo-300">
                      <IoMdSearch className="opacity-60 shrink-0" />
                      <input
                        type="text"
                        placeholder="Buscar por nome, telefone, e-mail ou documento…"
                        className="bg-transparent border-0 flex-1 text-sm py-1 focus:outline-none focus:ring-0 placeholder:text-gray-500"
                        onChange={(e) => setSearchName(e.target.value)}
                        value={searchName}
                      />
                      {searchName && (
                        <button
                          type="button"
                          className="text-xs text-gray-500 hover:text-gray-700"
                          onClick={() => {
                            setSearchName("");
                          }}
                        >
                          Limpar
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Chips dos filtros ativos */}
                  {activeFiltersCount > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {activeFilters.map((f, i) => (
                        <Chip
                          key={`${f.key}-${i}`}
                          label={f.label}
                          tone={f.tone}
                          onClear={() => removeSingleFilter(f.key)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </form>

      {/* Tabela */}
      {listCustomers?.length > 0 || isLoadingListCustomers ? (
        <>
          {/* Mobile listagem */}
          <div className="md:hidden mt-3 space-y-3">
            {isLoadingListCustomers
              ? Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={`mobile-skeleton-${index}`}
                    className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-sm"
                  >
                    <div className="h-4 w-2/3 bg-slate-200 rounded animate-pulse" />
                    <div className="mt-2 flex gap-2">
                      <div className="h-7 w-28 bg-slate-200 rounded-full animate-pulse" />
                      <div className="h-7 w-24 bg-slate-200 rounded-full animate-pulse" />
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="h-10 bg-slate-200 rounded-lg animate-pulse" />
                      <div className="h-10 bg-slate-200 rounded-lg animate-pulse" />
                    </div>
                    <div className="mt-3 h-8 bg-slate-200 rounded-lg animate-pulse" />
                  </div>
                ))
              : listCustomers.map((customer, index) => {
                  const displayName =
                    customer.typeCustomer === "CPF"
                      ? customer.name
                      : customer.socialReason;
                  const statusLabel =
                    customer.status === 1
                      ? "Ativo"
                      : customer.status === 0
                      ? "Bloqueado"
                      : "Inadimplente";
                  const statusTone =
                    customer.status === 1
                      ? "bg-emerald-50 text-emerald-700 ring-emerald-200/70"
                      : customer.status === 0
                      ? "bg-rose-50 text-rose-700 ring-rose-200/70"
                      : "bg-amber-50 text-amber-700 ring-amber-200/70";
                  const representativeName =
                    customer?.ParentCompany?.name ||
                    customer?.ParentCompany?.socialReason ||
                    "-";

                  return (
                    <div
                      key={`mobile-customer-${index}`}
                      className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">
                            {displayName}
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500">
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
                              {customer.typeCustomer}
                            </span>
                          </div>
                        </div>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset ${statusTone}`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              customer.status === 1
                                ? "bg-emerald-500"
                                : customer.status === 0
                                ? "bg-rose-500"
                                : "bg-amber-500"
                            }`}
                          />
                          {statusLabel}
                        </span>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2">
                          <div className="uppercase tracking-wide text-[10px] text-slate-500">
                            Dt. Cadastro
                          </div>
                          <div className="font-semibold text-slate-700">
                            {moment(customer.createdAt).format("DD/MM/yyyy")}
                            <span className="text-slate-500 font-normal">
                              {" "}
                              ({moment(customer.createdAt).fromNow()})
                            </span>
                          </div>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2">
                          <div className="uppercase tracking-wide text-[10px] text-slate-500">
                            Representante
                          </div>
                          <div
                            className="font-semibold text-slate-700 truncate"
                            title={representativeName}
                          >
                            {representativeName}
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between gap-2">
                        <div
                          className={`inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ring-1 ring-inset whitespace-nowrap ${
                            customer.typePayment === "PRE"
                              ? "text-emerald-700 bg-emerald-50 ring-emerald-100"
                              : "text-blue-700 bg-blue-50 ring-blue-100"
                          }`}
                        >
                          {customer.typePayment === "PRE" ? (
                            <>Pré-Pago - {formatToReal(customer?.credit)}</>
                          ) : (
                            <>
                              Pós-Pago{" "}
                              {customer?.vcm
                                ? `- vcm: ${formatToReal(customer?.vcm)}`
                                : ""}
                            </>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            data-tooltip-id="tooltip-permissions"
                            data-tooltip-content="Editar permissões de consulta"
                            onClick={() =>
                              handleEditPermissionQueries(customer)
                            }
                            className="p-2 rounded-lg border border-slate-200 bg-slate-50 text-blue-700 hover:border-blue-200 hover:bg-blue-50 transition duration-200"
                          >
                            <TbLockSearch size={18} />
                          </button>
                          <Tooltip id="tooltip-permissions" place="top" />

                          <button
                            data-tooltip-id="tooltip-view-customer"
                            data-tooltip-content="Visualizar detalhes do cliente"
                            onClick={() =>
                              navigate(`/admin/customers/${customer?.uuid}`)
                            }
                            className="p-2 rounded-lg border border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300 hover:bg-slate-100 transition duration-200"
                          >
                            <MdOutlineRemoveRedEye size={18} />
                          </button>
                          <Tooltip id="tooltip-view-customer" place="top" />
                        </div>
                      </div>
                    </div>
                  );
                })}
          </div>

          {/* Tabela desktop */}
          <div className="hidden md:block">
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white/90 mt-0 md:mt-3 shadow-sm">
              <Table
                striped={false}
                className="text-xs xl:text-sm min-w-[900px]"
              >
                <Table.Head className="bg-gradient-to-b from-slate-100 to-slate-200/70 uppercase text-[11px] tracking-wide">
                  <Table.HeadCell className="bg-transparent text-slate-600 font-semibold">
                    <button
                      type="button"
                      onClick={() => toggleSort("name")}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide"
                    >
                      Cliente
                      <span className="flex flex-col leading-none text-lg">
                        <MdArrowDropUp
                          className={getSortIconClass("name", "asc")}
                        />
                        <MdArrowDropDown
                          style={{ marginTop: "-12px" }}
                          className={getSortIconClass("name", "desc")}
                        />
                      </span>
                    </button>
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-transparent text-slate-600 font-semibold">
                    <button
                      type="button"
                      onClick={() => toggleSort("status")}
                      className="inline-flex items-center justify-center gap-1 text-[11px] font-semibold uppercase tracking-wide"
                    >
                      Status
                      <span className="flex flex-col leading-none text-lg">
                        <MdArrowDropUp
                          className={getSortIconClass("status", "asc")}
                        />
                        <MdArrowDropDown
                          style={{ marginTop: "-12px" }}
                          className={getSortIconClass("status", "desc")}
                        />
                      </span>
                    </button>
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-transparent text-slate-600 font-semibold">
                    <button
                      type="button"
                      onClick={() => toggleSort("createdAt")}
                      className="inline-flex items-center justify-center gap-1 text-[11px] font-semibold uppercase tracking-wide"
                    >
                      Dt. Cadastro
                      <span className="flex flex-col leading-none text-lg">
                        <MdArrowDropUp
                          className={getSortIconClass("createdAt", "asc")}
                        />
                        <MdArrowDropDown
                          style={{ marginTop: "-12px" }}
                          className={getSortIconClass("createdAt", "desc")}
                        />
                      </span>
                    </button>
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-transparent text-slate-600 font-semibold whitespace-nowrap">
                    Tipo Pgto.
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-transparent text-slate-600 font-semibold">
                    Ações
                  </Table.HeadCell>
                </Table.Head>

                <Table.Body className="divide-y divide-slate-200/70">
                  {!isLoadingListCustomers ? (
                    listCustomers?.length > 0 ? (
                      listCustomers.map((customer, index) => {
                        const displayName =
                          customer.typeCustomer === "CPF"
                            ? customer.name
                            : customer.socialReason;
                        const statusLabel =
                          customer.status === 1
                            ? "Ativo"
                            : customer.status === 0
                            ? "Bloqueado"
                            : "Inadimplente";
                        const statusTone =
                          customer.status === 1
                            ? "bg-emerald-50 text-emerald-700 ring-emerald-200/70"
                            : customer.status === 0
                            ? "bg-rose-50 text-rose-700 ring-rose-200/70"
                            : "bg-amber-50 text-amber-700 ring-amber-200/70";

                        return (
                          <Table.Row
                            key={index}
                            className="transition bg-white even:bg-slate-50/40 hover:bg-slate-100/60"
                          >
                            {/* Cliente */}
                            <Table.Cell className="text-slate-700 py-2">
                              <div className="flex items-start gap-3">
                                <div className="min-w-0">
                                  <div className="font-semibold text-slate-800 truncate">
                                    {displayName}
                                  </div>
                                  <div className="text-2xs -mt-1">
                                    Representante:{" "}
                                    {customer?.ParentCompany?.name ||
                                    customer?.ParentCompany?.socialReason ? (
                                      <span
                                        className=""
                                        title={
                                          customer.ParentCompany.name ||
                                          customer.ParentCompany.socialReason
                                        }
                                      >
                                        {customer.ParentCompany.name ||
                                          customer.ParentCompany.socialReason}
                                      </span>
                                    ) : (
                                      <span className="text-slate-400">-</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </Table.Cell>

                            {/* Status */}
                            <Table.Cell className="text-center text-slate-700 py-2">
                              <span
                                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset ${statusTone}`}
                              >
                                <span
                                  className={`h-1.5 w-1.5 rounded-full ${
                                    customer.status === 1
                                      ? "bg-emerald-500"
                                      : customer.status === 0
                                      ? "bg-rose-500"
                                      : "bg-amber-500"
                                  }`}
                                />
                                {statusLabel}
                              </span>
                            </Table.Cell>

                            {/* Cadastro */}
                            <Table.Cell className="text-center text-slate-700 py-2">
                              <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                                {moment(customer.createdAt).format(
                                  "DD/MM/yyyy"
                                )}
                                <span className="text-slate-500 font-normal">
                                  ({moment(customer.createdAt).fromNow()})
                                </span>
                              </span>
                            </Table.Cell>

                            {/* Tipo Pgto. */}
                            <Table.Cell className="text-center py-2">
                              <div className="flex items-center justify-center gap-1.5">
                                <div
                                  className={`inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ring-1 ring-inset whitespace-nowrap ${
                                    customer.typePayment === "PRE"
                                      ? "text-emerald-700 bg-emerald-50 ring-emerald-100"
                                      : "text-blue-700 bg-blue-50 ring-blue-100"
                                  }`}
                                >
                                  {customer.typePayment === "PRE" ? (
                                    <>
                                      Pré-Pago -{" "}
                                      {formatToReal(customer?.credit)}
                                    </>
                                  ) : (
                                    <>
                                      Pós-Pago{" "}
                                      {customer?.vcm
                                        ? `- vcm: ${formatToReal(
                                            customer?.vcm
                                          )}`
                                        : ""}
                                    </>
                                  )}
                                </div>
                              </div>
                            </Table.Cell>

                            {/* Ações */}
                            <Table.Cell className="text-center py-2">
                              <div className="flex justify-center items-center gap-1.5">
                                <button
                                  data-tooltip-id="tooltip-permissions"
                                  data-tooltip-content="Editar permissões de consulta"
                                  onClick={() =>
                                    handleEditPermissionQueries(customer)
                                  }
                                  className="p-1.5 rounded-lg border border-slate-200 bg-slate-50 text-blue-700 hover:border-blue-200 hover:bg-blue-50 transition duration-200"
                                >
                                  <TbLockSearch size={18} />
                                </button>
                                <Tooltip id="tooltip-permissions" place="top" />

                                <button
                                  data-tooltip-id="tooltip-view-customer"
                                  data-tooltip-content="Visualizar detalhes do cliente"
                                  onClick={() =>
                                    navigate(
                                      `/admin/customers/${customer?.uuid}`
                                    )
                                  }
                                  className="p-1.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300 hover:bg-slate-100 transition duration-200"
                                >
                                  <MdOutlineRemoveRedEye size={18} />
                                </button>
                                <Tooltip
                                  id="tooltip-view-customer"
                                  place="top"
                                />
                              </div>
                            </Table.Cell>
                          </Table.Row>
                        );
                      })
                    ) : (
                      <Table.Row>
                        <Table.Cell
                          colSpan={6}
                          className="text-center py-6 text-slate-400"
                        >
                          Nenhum cliente encontrado.
                        </Table.Cell>
                      </Table.Row>
                    )
                  ) : (
                    <TableLoading cols={5} />
                  )}
                </Table.Body>
              </Table>
            </div>
          </div>

          {/* Rodapé: contagem + paginação */}
          {totalCount > 0 && (
            <div className="mt-3 text-center text-sm text-gray-600">
              Exibindo {(currentPage - 1) * 20 + 1}–
              {Math.min(currentPage * 20, totalCount)} de {totalCount} registros
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex overflow-x-auto justify-center mt-3">
              <Pagination
                layout="pagination"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                previousLabel=""
                nextLabel=""
                showIcons
              />
            </div>
          )}
        </>
      ) : (
        <EmptyTable icon="person_off">Nenhum cliente cadastrado</EmptyTable>
      )}

      {/* Drawers & Modal */}
      <Drawer
        title={`Visualizar faturamento`}
        onClose={setIsDrawerViewInvoice}
        isOpen={isDrawerViewInvoice}
        className={"w-full md:w-4/6 lg:w-3/6 xl:w-2/6"}
      >
        <CompanyInvoiceForm
          company={selectedCompany}
          onClose={setIsDrawerViewInvoice}
          isDrawerOpen={isDrawerViewInvoice}
          companyType="CUSTOMER"
        />
      </Drawer>

      <Modal
        show={isDrawerPermissionQueries}
        onClose={() => setIsDrawerPermissionQueries(false)}
        size="4xl"
      >
        <Modal.Header>Permissões</Modal.Header>
        <Modal.Body>
          <CompanyQueriesForm
            company={selectedCompany}
            categories={categories}
            onClose={() => setIsDrawerPermissionQueries(false)}
            fetchCompanies={() =>
              queryClient.invalidateQueries(["listCustomers", currentPage])
            }
          />
        </Modal.Body>
      </Modal>

      <Drawer
        title={`Editar cliente`}
        onClose={setIsDrawerEditCustomer}
        isOpen={isDrawerEditCustomer}
        className={"w-full md:w-4/6 lg:w-3/6"}
      >
        <CompanyEditForm
          company={selectedCompany}
          onClose={setIsDrawerEditCustomer}
          categories={categories}
          fetchCompanies={() =>
            queryClient.invalidateQueries(["listCustomers", currentPage])
          }
        />
      </Drawer>

      <ModalDelete
        title={`Deseja ${
          typeBlock === 0 ? "bloquear" : "desbloquear"
        } este cliente?`}
        isOpen={isOpenModalBlock}
        setOpenModal={setOpenModalBlock}
        isLoading={isLoadingBlock}
        handleDelete={() => blockUnlockCustomer(typeBlock)}
      />
    </>
  );
};

export default AdminCustomersTable;
